import { NumberInput } from '@statsbomb/kitbag-components';
import { DistroSlider } from '@statsbomb/kitbag-datavis';
import { MetricDistributionQuartiles } from '@statsbomb/parachute-types';
import { round } from 'lodash';
import { FocusEvent, ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';
import { roundToFixed } from '@/components/modal/CreateRadarTemplateModal/metricLimitsUtils';
import { toDisplayValue } from '@/components/sliders/sliderUtils';
import { MinMax } from '@/types/metric';
import { FormattedMetricDistributionBucket } from '@/utils/metrics';
import { formatRadarValue } from '@/utils/radar';

const translations = 'createRadarTemplateModal.step3';

const calculateXAxisLimit = (metricBucketsMinOrMax: number, isPercentage: boolean, precision: number) => {
  const displayMinOrMax = isPercentage ? metricBucketsMinOrMax * 100 : metricBucketsMinOrMax;
  return round(displayMinOrMax, precision);
};

/**
 * Detects if a NumberInput change came from a +/- button click (vs typing).
 * Button clicks increment/decrement by exactly one step, so we check if the difference between values equals the step
 * size (with small tolerance for floating point precision issues).
 */
const changeIsFromButtonClick = (step: number, previousValue: number, displayValue: number) => {
  const isValidNumber = !Number.isNaN(displayValue) && !Number.isNaN(previousValue);
  if (!isValidNumber) return false;

  const diff = Math.abs(displayValue - previousValue);
  const epsilon = step * 0.01; // 1% tolerance
  return Math.abs(diff - step) < epsilon;
};

/** Parse the draft value, fall back to the atom value if the draft isn't a valid number */
const parseDraftOrUseAtom = (draft: string | null, atom: number): number => {
  if (draft === null) return atom;
  const parsed = parseFloat(draft);
  return Number.isNaN(parsed) ? atom : parsed;
};

/** Render and controls custom metrics section (NumberInputs for upper and lower values, plus the DistroSlider) */
export const MetricLimitsCustomControls = ({
  activeMetric,
  customLimits,
  isReversed,
  metricBuckets,
  isDisabled,
  isPlayerSection,
  precision,
  step,
  atomMin,
  atomMax,
  isPercentage,
  quartiles,
  onLimitsChange,
}: {
  activeMetric: string;
  customLimits: Record<string, MinMax>;
  isReversed: boolean;
  metricBuckets: FormattedMetricDistributionBucket;
  isDisabled: boolean;
  isPlayerSection: boolean;
  precision: number;
  step: number;
  atomMin: number;
  atomMax: number;
  isPercentage: boolean;
  quartiles: MetricDistributionQuartiles;
  onLimitsChange: (e: ChangeEvent<HTMLInputElement>, minMaxValues: [number, number]) => void;
}) => {
  const { t } = useTranslation(['modal', 'metrics']);

  // Calculate slider bounds (needed for clamping logic)
  const xAxisMin = calculateXAxisLimit(metricBuckets.min, isPercentage, precision);
  const xAxisMax = calculateXAxisLimit(metricBuckets.max, isPercentage, precision);

  // Local state to hold draft values while user is typing (before blur/commit)
  // Store as strings to allow partial values like '-' or '.' while typing
  const [draftMin, setDraftMin] = useState<string | null>(null);
  const [draftMax, setDraftMax] = useState<string | null>(null);

  // Display values: use draft if available, otherwise use atom values
  const displayMin = draftMin !== null ? draftMin : roundToFixed(atomMin, precision);
  const displayMax = draftMax !== null ? draftMax : roundToFixed(atomMax, precision);

  // Initialize or clear draft state based on current mode and custom limits
  useEffect(() => {
    if (!isDisabled && customLimits[activeMetric]) {
      // In custom mode with existing limits - initialize draft from those limits
      const { min, max } = customLimits[activeMetric];
      const displayMin = toDisplayValue(min, isPercentage);
      const displayMax = toDisplayValue(max, isPercentage);
      setDraftMin(roundToFixed(displayMin, precision));
      setDraftMax(roundToFixed(displayMax, precision));
    } else {
      // Not in custom mode or no custom limits yet - clear draft
      setDraftMin(null);
      setDraftMax(null);
    }
  }, [activeMetric, isDisabled, customLimits, isPercentage, precision]);

  /** Clamps value to slider bounds and prevents crossover, used by onChange (button clicks) and onBlur handlers */
  const clampValue = (value: number, isLower: boolean, otherValue: number): number => {
    // First clamp to slider bounds
    const boundedValue = Math.max(xAxisMin, Math.min(xAxisMax, value));

    // Then prevent crossover (lower can't exceed upper, upper can't go below lower)
    return isLower
      ? Math.min(boundedValue, round(otherValue - step, precision))
      : Math.max(boundedValue, round(otherValue + step, precision));
  };

  /**
   * onChange handler that:
   * - Detects button clicks (+/-) and commits immediately with clamping
   * - Stores typed values in draft state without committing (waits for blur)
   */
  const createNumberInputChangeHandler = (isLower: boolean) => (event: ChangeEvent<HTMLInputElement>) => {
    const newRawValue = event.target.value;
    const newDisplayValue = parseFloat(newRawValue);

    // Get previous displayed value (draft if available, otherwise rounded atom value)
    // IMPORTANT: Must round atom values to match what NumberInput displays
    const [draft, atom] = isLower ? [draftMin, atomMin] : [draftMax, atomMax];
    const previousValue = draft !== null ? parseFloat(draft) : round(atom, precision);

    if (changeIsFromButtonClick(step, previousValue, newDisplayValue)) {
      // A '+' or '-' button has been clicked: commit immediately with clamping
      const otherMin = parseDraftOrUseAtom(draftMin, atomMin);
      const otherMax = parseDraftOrUseAtom(draftMax, atomMax);

      const clampedValue = clampValue(newDisplayValue, isLower, isLower ? otherMax : otherMin);
      const newRange: [number, number] = isLower ? [clampedValue, otherMax] : [otherMin, clampedValue];

      onLimitsChange(event, newRange);
    } else if (isLower) {
      // User is typing into the lower input: store in draft
      setDraftMin(newRawValue);
    } else {
      // User is typing into the upper input: store in draft
      setDraftMax(newRawValue);
    }
  };

  /**
   * onBlur handler that validates and commits draft values:
   * - If valid: clamp and commit to atoms
   * - If invalid (NaN): revert to atom value (no change)
   */
  const createNumberInputBlurHandler = (isLower: boolean) => (event: FocusEvent<HTMLInputElement>) => {
    const displayValue = parseFloat(event.target.value);

    if (Number.isNaN(displayValue)) {
      // If displayValue is invalid, revert to atom values
      const revertValue = isLower ? atomMin : atomMax;
      const otherValue = isLower ? atomMax : atomMin;
      const newRange: [number, number] = isLower ? [revertValue, otherValue] : [otherValue, revertValue];
      onLimitsChange(event, newRange);
    } else {
      // Valid number: clamp and commit
      const otherMin = parseDraftOrUseAtom(draftMin, atomMin);
      const otherMax = parseDraftOrUseAtom(draftMax, atomMax);

      const clampedValue = clampValue(displayValue, isLower, isLower ? otherMax : otherMin);
      const newRange: [number, number] = isLower ? [clampedValue, otherMax] : [otherMin, clampedValue];

      onLimitsChange(event, newRange);
    }
  };

  // Create handlers for lower and upper inputs
  const handleLowerNumberInputChange = createNumberInputChangeHandler(true);
  const handleUpperNumberInputChange = createNumberInputChangeHandler(false);
  const handleLowerNumberInputBlur = createNumberInputBlurHandler(true);
  const handleUpperNumberInputBlur = createNumberInputBlurHandler(false);

  // For normal metrics the left input is min and right input is max. For reversed metrics, this is swapped.
  const [leftInputValue, rightInputValue] = isReversed ? [displayMax, displayMin] : [displayMin, displayMax];
  const [handleLeftNumberInputChange, handleRightNumberInputChange] = isReversed
    ? [handleUpperNumberInputChange, handleLowerNumberInputChange]
    : [handleLowerNumberInputChange, handleUpperNumberInputChange];
  const [handleLeftNumberInputBlur, handleRightNumberInputBlur] = isReversed
    ? [handleUpperNumberInputBlur, handleLowerNumberInputBlur]
    : [handleLowerNumberInputBlur, handleUpperNumberInputBlur];

  // Determine labels and IDs based on reversal
  const [leftLabelKey, leftIdPrefix] = isReversed ? ['upperLimit', 'upper-limit'] : ['lowerLimit', 'lower-limit'];
  const [rightLabelKey, rightIdPrefix] = isReversed ? ['lowerLimit', 'lower-limit'] : ['upperLimit', 'upper-limit'];

  // Always pass values as [min, max] regardless of slider reversal - DistroSlider handles isReversed internally
  const sliderValue: [number, number] = [atomMin, atomMax];

  // prevents the user from setting the slider min and max to the same value.
  const onSliderChange = (e: ChangeEvent<HTMLInputElement>, newRange: [number, number]) => {
    const min = atomMin === newRange[0] ? atomMin : clampValue(newRange[0], true, atomMax);
    const max = atomMax === newRange[1] ? atomMax : clampValue(newRange[1], false, atomMin);
    onLimitsChange(e, [min, max]);
  };

  const metricQuartiles = {
    p25: formatRadarValue(quartiles.p25, isPercentage),
    median: formatRadarValue(quartiles.median, isPercentage),
    p75: formatRadarValue(quartiles.p75, isPercentage),
  };

  return (
    <div className="border p-4">
      <div className={twJoin('max-w-72', isDisabled && 'opacity-sb-disabled')}>
        <div className="flex gap-2 mb-4 justify-between">
          <NumberInput
            id={`${leftIdPrefix}-${activeMetric}`}
            label={t(`${translations}.${leftLabelKey}`)}
            value={leftInputValue}
            step={step}
            inputDigits={6}
            labelPosition="top"
            disabled={isDisabled}
            onChange={handleLeftNumberInputChange}
            onBlur={handleLeftNumberInputBlur}
            increaseAriaLabel={t('increment', { ns: 'accessibility' })}
            decreaseAriaLabel={t('decrement', { ns: 'accessibility' })}
          />
          <NumberInput
            id={`${rightIdPrefix}-${activeMetric}`}
            label={t(`${translations}.${rightLabelKey}`)}
            value={rightInputValue}
            step={step}
            inputDigits={6}
            labelPosition="top"
            disabled={isDisabled}
            onChange={handleRightNumberInputChange}
            onBlur={handleRightNumberInputBlur}
            increaseAriaLabel={t('increment', { ns: 'accessibility' })}
            decreaseAriaLabel={t('decrement', { ns: 'accessibility' })}
          />
        </div>
        <DistroSlider
          testId="radar-metric-distribution"
          id={`distro-slider-${activeMetric}`}
          buckets={metricBuckets.buckets}
          min={xAxisMin}
          max={xAxisMax}
          step={step}
          value={sliderValue}
          onChange={onSliderChange}
          isDisabled={isDisabled}
          isReversed={isReversed}
          quartiles={metricQuartiles}
          isPushable
          label={t(`${translations}.sliderTitle.${isPlayerSection ? 'player' : 'team'}`)}
          labels={[
            {
              label: roundToFixed(xAxisMin, precision),
              position: xAxisMin,
            },
            {
              label: roundToFixed(xAxisMax, precision),
              position: xAxisMax,
            },
          ]}
        />
      </div>
    </div>
  );
};
